import { oc } from "@orpc/contract";
import { z } from "zod";
import { LeadTimeSourceSchema } from "./lead-times.contract";

// ─── Shared schemas ─────────────────────────────────────────────────────────

export const SupplierProductRowSchema = z.object({
  id: z.string().uuid(),
  supplierName: z.string(),
  /** ProductV3 key (the SKU) — the row's real product identity. */
  productV3Key: z.string().nullable(),
  productSku: z.string().nullable(),
  productName: z.string(),
  warehouseId: z.string().uuid().nullable(),
  warehouseName: z.string().nullable(),
  unitPrice: z.number(),
  minimumOrderQuantity: z.number().nullable(),
  incrementalOrderQuantity: z.number().nullable(),
  /**
   * Observed lead time resolved through the im_lead_time_stats ladder for
   * this row's warehouse (same source as the detail page), NOT the GILEAD
   * catalog field. Null when the row has no warehouse or no rung answered.
   */
  leadTimeDays: z.number().nullable(),
  priceBreakCount: z.number(),
});

export type SupplierProductRow = z.infer<typeof SupplierProductRowSchema>;

// ─── List ───────────────────────────────────────────────────────────────────

export const ListSupplierProductsInputSchema = z.object({
  warehouseId: z.string().uuid().optional(),
  eligibleWarehouseId: z.string().uuid().optional(),
  supplierId: z.string().uuid().optional(),
  search: z.string().optional(),
  warehouseNames: z.array(z.string()).optional(),
  supplierNames: z.array(z.string()).optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export const ListSupplierProductsResponseSchema = z.object({
  rows: z.array(SupplierProductRowSchema),
  totalRows: z.number(),
});

export type ListSupplierProductsInput = z.infer<
  typeof ListSupplierProductsInputSchema
>;
export type ListSupplierProductsResponse = z.infer<
  typeof ListSupplierProductsResponseSchema
>;

// ─── Detail (by product) ────────────────────────────────────────────────────

/** Address a product by its SKU (docs/procurement/product-v3-only.md). */
export const GetSupplierProductDetailInputSchema = z.object({
  productV3Key: z.string().min(1),
});

const PriceBreakSchema = z.object({
  tier: z.number(),
  minQuantity: z.number(),
  unitCost: z.number(),
});

const SupplierForProductSchema = z.object({
  supplierProductId: z.string().uuid(),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  warehouseId: z.string().uuid().nullable(),
  warehouseName: z.string().nullable(),
  unitPrice: z.number(),
  minimumOrderQuantity: z.number().nullable(),
  incrementalOrderQuantity: z.number().nullable(),
  /**
   * Observed lead time for this row's warehouse, resolved from
   * `im_lead_time_stats` via the same ladder the engines and the detail
   * dialog use — NOT the GAINS `GILEAD` catalog field, which is unit-mangled
   * (likely weeks) and ~3% coverage (docs/procurement/data-quality.md).
   * Null when the row carries no warehouse to scope the ladder to, or when
   * no rung answered (source 'none': surfaces show "no estimate").
   */
  leadTimeDays: z.number().nullable(),
  /** Ladder rung the resolution came from, 'override' for a manual pin,
   * 'none' = nothing answered. */
  leadTimeSource: LeadTimeSourceSchema.nullable(),
  /** Closed receipts behind the estimate (0 when source = 'none'). */
  leadTimeSampleN: z.number().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  priceBreaks: z.array(PriceBreakSchema),
});

export const GetSupplierProductDetailResponseSchema = z.object({
  /** ProductV3 key (the SKU): the product's identity. */
  productV3Key: z.string().nullable(),
  productSku: z.string().nullable(),
  productName: z.string(),
  isActive: z.boolean(),
  suppliers: z.array(SupplierForProductSchema),
});

export type GetSupplierProductDetailInput = z.infer<
  typeof GetSupplierProductDetailInputSchema
>;
export type GetSupplierProductDetailResponse = z.infer<
  typeof GetSupplierProductDetailResponseSchema
>;
export type SupplierForProduct = z.infer<typeof SupplierForProductSchema>;
export type PriceBreak = z.infer<typeof PriceBreakSchema>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const supplierProductsContracts = {
  list: oc
    .input(ListSupplierProductsInputSchema)
    .output(ListSupplierProductsResponseSchema)
    .route({
      method: "GET",
      summary: "List supplier products",
      description:
        "Paged supplier × product × warehouse rows with unit price, minimum and incremental order quantity, observed lead time and price-break count. Filter by supplier, warehouse, search.",
    }),
  getByProduct: oc
    .input(GetSupplierProductDetailInputSchema)
    .output(GetSupplierProductDetailResponseSchema)
    .route({
      method: "GET",
      summary: "Get suppliers for a product",
      description:
        "For one SKU, lists every supplier with per-warehouse unit price, minimum order quantity, observed lead time and its source, and quantity price breaks.",
    }),
} as const;
